import 'bootstrap/dist/css/bootstrap-grid.min.css';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import * as AuthActions from '~/actions/auth';
import { v4 as uuid } from 'uuid';
import './app.less';
import * as Sentry from '@sentry/react';

import RouterApp from './Router';
import MaintenanceScreen from './app/components/MaintenanceScreen/MaintenanceScreen';
import AlertModalProvider from './app/components/AlertModalProvider/AlertModalProvider';
import * as StorageService from '@portal/services/storage';
import { handleMobileAccess } from '@portal/services';
import useReduxState from '@portal/hooks/useReduxState';
import { SettingsActions } from '@portal/actions';
import { customStyles as customStylesFlag } from '@portal/config/app.json';
import { VITE_APPLICATION_NAME } from '@portal/config/env';
import { SET_THEME } from '@portal/actions/actionTypes';
import { history } from '@portal/services/browserHistory';
import { waitIntegrityReady } from '@portal/repositories/securitySdkConfig';

const App = () => {
  const dispatch = useDispatch();
  const {
    settings: { remoteConfig },
    user: { me: loggedUser, roles },
  } = useReduxState();

  useEffect(() => {
    if (!loggedUser?.login) {
      Sentry.setUser({});
      return;
    }

    const account = loggedUser.account;
    const setSentryUser = (location?: {
      latitude: number;
      longitude: number;
    }) => {
      Sentry.setUser({
        id: loggedUser.id ?? undefined,
        username: loggedUser.login ?? undefined,
        email: loggedUser.email ?? account?.email ?? undefined,
        ip_address: '{{auto}}',
        data: {
          name: loggedUser.name ?? account?.name ?? undefined,
          cellphone: loggedUser.cellphone ?? account?.cellphone ?? undefined,
          accountId: loggedUser.accountId ?? undefined,
          accountType: account?.type ?? undefined,
          accountStatus: account?.status ?? undefined,
          taxIdentifier: account?.taxIdentifier ?? undefined,
          accountIdFromAccount: account?.id ?? undefined,
          city: account?.address?.city ?? undefined,
          state: account?.address?.state ?? undefined,
          roles: roles?.join(', ') ?? undefined,
          canAccessPix: loggedUser.canAccessPix ?? undefined,
          ...(location && {
            latitude: location.latitude,
            longitude: location.longitude,
          }),
        },
      });
    };

    setSentryUser();
    if (account?.type) {
      Sentry.setTag('account.type', account.type);
    }
    if (account?.status) {
      Sentry.setTag('account.status', account.status);
    }

    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSentryUser({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {}
      );
    }
  }, [loggedUser, roles]);

  useEffect(() => {
    const unlisten = history.listen(({ pathname }) => {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: pathname,
        level: 'info',
        data: { pathname },
      });
    });
    return () => unlisten();
  }, []);

  useEffect(() => {
    if (!StorageService.getDeviceId()) {
      StorageService.setItem('device-id', uuid());
    }

    waitIntegrityReady().then(() => {
      dispatch(AuthActions.checkIsLogged());
    });
  }, [dispatch]);

  useEffect(() => {
    const theme = localStorage.getItem('selected-theme');
    dispatch({ type: SET_THEME, payload: theme });
  }, [dispatch]);

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }

    handleMobileAccess(remoteConfig);
  }, [dispatch, remoteConfig]);

  useEffect(() => {
    if (customStylesFlag.darkMode) {
      document.documentElement.classList.add('darkMode');
    } else {
      document.documentElement.classList.remove('darkMode');
    }
  }, []);

  if (remoteConfig?.isUnderMaintenance) {
    return <MaintenanceScreen />;
  }

  return (
    <div>
      {VITE_APPLICATION_NAME.toUpperCase().includes('STG') && (
        <div className="stg-banner">Staging</div>
      )}
      <AlertModalProvider />
      <RouterApp />
    </div>
  );
};

export default App;
